// pages/Home.jsx
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Stack from "react-bootstrap/Stack";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Collapse from "react-bootstrap/Collapse";
import "./home.css";
import ListView from "../Components/FullCalendar/ListView";
import { PatientSearch } from "../Components/SearchPatients/SearchPatients";
import TasksSection from "../Components/Tasks/TasksSection";
import { userCanUseTasks } from "../Components/Tasks/access";
import InboxSection from "../Components/WhatsApp/InboxSection";
import { userCanUseWhatsApp } from "../Components/WhatsApp/access";
import { useLocation, useNavigate } from "react-router-dom";
import { AddPatientSteps } from "../Components/PatientsAccounts/AddPatientInfo";
import {
  getAgedInvoicesByPracticeNumbers,
  getAllPaymentLinks, // still imported if you need later
  getAllClaims,
} from "../afinanceapi";
import { getUserPracticeById } from "../api";
import CreateReceiptModal from "../Components/Accounts/Receipt";
import { jwtDecode } from "jwt-decode";

const SectionCard = ({ title, subtitle, children, className, left, right }) => (
  <Card className={`section-card mb-3 border-0 rounded-0 ${className || ""}`}>
    <Card.Header className="bg-white border-0 section-card__header">
      <Stack direction="horizontal" gap={2} className="align-items-start w-100">
        {left && <div className="d-flex align-items-center">{left}</div>}
        <div className="pe-2">
          {title && <h6 className="text-primary mb-0 fw-semibold">{title}</h6>}
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        {right && <div className="ms-auto">{right}</div>}
      </Stack>
    </Card.Header>
    <Card.Body className="section-card__body">{children}</Card.Body>
  </Card>
);

// --- keep your imports and SectionCard as-is ---

const StatCard = ({
  label,
  value,
  tone = "primary",
  onClick,
  disabled,
  className = "",
  dense = false, // NEW
}) => {
  const interactive = !!onClick && !disabled;
  return (
    <Card
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      className={`stat-card ${dense ? "stat-card--dense" : ""} mb-2 rounded-0 ${
        interactive ? "stat-card--link" : ""
      } ${className}`}
      aria-label={`${label} ${value}`}
    >
      <Card.Body className="stat-card__body">
        <div className="d-flex align-items-center stat-card__row">
          <div className="me-2 stat-label">{label}</div>
          <div className="ms-auto fw-semibold text-end">
            <span className={`text-${tone} stat-value`}>
              R {Number(value || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// helper: detect Bootstrap "mobile" (<= 767.98px)
function useIsMobile() {
  const query = "(max-width: 767.98px)";
  const get = () => window.matchMedia(query).matches;

  const [isMobile, setIsMobile] = React.useState(get());

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  return isMobile;
}

export function Home() {
  const [patient, setPatient] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [totals, setTotals] = useState({});
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [claimTotals, setClaimTotals] = useState({ submitted: 0, paid: 0 });
  const [userRole, setUserRole] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [showAgeAnalysis, setShowAgeAnalysis] = useState(() => localStorage.getItem("home_showAgeAnalysis") !== "false");
  const [showClaims, setShowClaims] = useState(() => localStorage.getItem("home_showClaims") !== "false");
  const isMobile = useIsMobile();
  const canNavigate = userRole && !isMobile; // <- no nav to reports on mobile

  // Deep link from notification bell — Notification.jsx navigates to
  // `/home?taskId=…` for type='task'. Pass it down to TasksSection which
  // opens the modal, then clear via onOpenTaskHandled so we don't re-open if
  // the user closes the modal and the URL hash hasn't changed.
  const taskIdFromUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('taskId') || null;
  }, [location.search]);
  const [openTaskId, setOpenTaskId] = useState(null);
  React.useEffect(() => { setOpenTaskId(taskIdFromUrl); }, [taskIdFromUrl]);

  // Tasks feature gate — drives both the dashboard layout (Patient Search
  // moves to top of 3rd col when on) AND whether TasksSection / its data
  // ever load. Backend enforces independently via requireTasksFeature.
  const canUseTasks = React.useMemo(() => userCanUseTasks(), []);
  const canUseWhatsApp = React.useMemo(() => userCanUseWhatsApp(), []);

  useEffect(() => {
    const user = jwtDecode(sessionStorage.getItem("user"));
    const practiceNumbers = user.practice;

    const rolesFromRole = Array.isArray(user?.role)
      ? user.role
      : user?.role
      ? [user.role]
      : [];
    const rolesFromPermissions = Array.isArray(user?.permissions)
      ? user.permissions
      : [];
    const hasAllowedRole = [
      ...new Set([...rolesFromRole, ...rolesFromPermissions]),
    ].some((r) => ["SuperAdmin", "Finance"].includes(r));
    setUserRole(hasAllowedRole);

    function useIsMobile() {
      const query = "(max-width: 767.98px)";
      const get = () => window.matchMedia(query).matches;

      const [isMobile, setIsMobile] = React.useState(get());

      React.useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e) => setIsMobile(e.matches);
        if (mql.addEventListener) mql.addEventListener("change", handler);
        else mql.addListener(handler);
        return () => {
          if (mql.removeEventListener)
            mql.removeEventListener("change", handler);
          else mql.removeListener(handler);
        };
      }, []);

      return isMobile;
    }

    // Only fetch age analysis and claims data for authorized roles
    if (hasAllowedRole) {
      (async () => {
        try {
          // Age Analysis
          const ai = await getAgedInvoicesByPracticeNumbers(practiceNumbers);
          const agg = ai.reduce(
            (acc, inv) => ({
              days30: acc.days30 + (inv.days30 || 0),
              days60: acc.days60 + (inv.days60 || 0),
              days90: acc.days90 + (inv.days90 || 0),
              days120plus: acc.days120plus + (inv.days120plus || 0),
            }),
            { days30: 0, days60: 0, days90: 0, days120plus: 0 }
          );
          setTotals(agg);

          // Claims
          const practiceIds = await getUserPracticeById(user._id);
          const idArray = practiceIds.map((p) => p._id);
          const claims = await getAllClaims(idArray);
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          const filtered = (claims || []).filter(
            (c) => new Date(c.createdAt) >= oneMonthAgo
          );

          let submitted = 0;
          let paid = 0;
          filtered.forEach((c) => {
            // Use claim-level submitted gross (same field as Claims report)
            // so the dashboard total matches what the user sees on click-through.
            submitted += Number(c.financials?.submittedAmount || 0);
            paid += Number(c.totalProviderPaid || 0);
          });
          setClaimTotals({ submitted, paid });
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, []);

  return (
    <div className="container-fluid home-wrap">
      {/* tighter vertical gutters on mobile, normal on md+ */}
      <Row className="gx-3 gy-2 gy-md-3 with-vdividers">
        {/* Left: Today list */}
        <Col lg={4}>
          <SectionCard title="Today's Appointments">
            <ListView key={listRefreshKey} />
          </SectionCard>
        </Col>

        {/* Middle column.
            - If user has the tasks feature: Tasks section renders here and
              Patient Search moves to the top of the 3rd column.
            - Otherwise: Patient Search stays in the middle column (legacy
              layout) and Tasks never mounts (so non-feature users never
              hit the /api/tasks endpoints either). */}
        <Col lg={4}>
          {canUseTasks ? (
            <TasksSection
              openTaskId={openTaskId}
              onOpenTaskHandled={() => setOpenTaskId(null)}
            />
          ) : (
            <SectionCard
              className="mobile-top-divider mb-0"
              title="Patient Search"
              subtitle="Find a patient by name or ID"
              right={
                <Button
                  variant="primary"
                  size="sm"
                  className="d-inline-flex align-items-center gap-2"
                  onClick={() => setModalShow(true)}
                >
                  <i className="fa-solid fa-user-plus" />
                  <span>Add Patient</span>
                </Button>
              }
            >
              <div className="mb-2">
                <PatientSearch
                  onPatientSelect={(p) => {
                    if (!p) return;
                    setPatient(p);
                    navigate(`/patients/appointment/${p._id}/1`, { state: { fromDashboard: true } });
                  }}
                />
              </div>
            </SectionCard>
          )}
        </Col>

        {/* Right column.
            - When tasks feature is on, Patient Search is the first card here
              (above the userRole-gated Invoices/Claims block). It renders
              regardless of role so non-finance tasks-users still get search.
            - When tasks feature is off, this column only contains the
              userRole-gated finance widgets (legacy behaviour). */}
        <Col lg={4} className="with-hdividers">
          {canUseTasks && (
            <SectionCard
              className="mobile-top-divider mb-0"
              title="Patient Search"
              subtitle="Find a patient by name or ID"
              right={
                <Button
                  variant="primary"
                  size="sm"
                  className="d-inline-flex align-items-center gap-2"
                  onClick={() => setModalShow(true)}
                >
                  <i className="fa-solid fa-user-plus" />
                  <span>Add Patient</span>
                </Button>
              }
            >
              <div className="mb-2">
                <PatientSearch
                  onPatientSelect={(p) => {
                    if (!p) return;
                    setPatient(p);
                    navigate(`/patients/appointment/${p._id}/1`, { state: { fromDashboard: true } });
                  }}
                />
              </div>
            </SectionCard>
          )}
          {canUseWhatsApp && <InboxSection />}
          {userRole && (
            <>
              <SectionCard
                className="mobile-top-divider"
                title={
                  <button
                    type="button"
                    className="section-card__title-toggle"
                    onClick={() => setShowAgeAnalysis((v) => { localStorage.setItem("home_showAgeAnalysis", String(!v)); return !v; })}
                    aria-expanded={showAgeAnalysis}
                    aria-label={showAgeAnalysis ? 'Collapse age analysis' : 'Expand age analysis'}
                  >
                    Outstanding Invoices
                    <i className={`fa-solid fa-chevron-${showAgeAnalysis ? "up" : "down"} ms-2`} />
                  </button>
                }
                subtitle="Age analysis"
                right={
                  !isMobile && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="d-inline-flex align-items-center gap-2"
                      onClick={() => setReceiptModalShow(true)}
                    >
                      <i className="fa-solid fa-file-invoice-dollar" />
                      <span>Create Receipt</span>
                    </Button>
                  )
                }
              >
                <Collapse in={showAgeAnalysis}>
                  <div>
                    <Row xs={2} md={2} className="g-2 mb-0">
                      <Col>
                        <StatCard
                          dense
                          label="30 Days"
                          value={totals.days30}
                          tone="success"
                          onClick={
                            canNavigate
                              ? () => navigate("/billing?aging=30")
                              : undefined
                          }
                          disabled={!canNavigate}
                        />
                      </Col>
                      <Col>
                        <StatCard
                          dense
                          label="60 Days"
                          value={totals.days60}
                          tone="warning"
                          onClick={
                            canNavigate
                              ? () => navigate("/billing?aging=60")
                              : undefined
                          }
                          disabled={!canNavigate}
                        />
                      </Col>
                      <Col>
                        <StatCard
                          dense
                          label="90 Days"
                          value={totals.days90}
                          tone="danger"
                          onClick={
                            canNavigate
                              ? () => navigate("/billing?aging=90")
                              : undefined
                          }
                          disabled={!canNavigate}
                        />
                      </Col>
                      <Col>
                        <StatCard
                          dense
                          label="120+ Days"
                          value={totals.days120plus}
                          tone="danger"
                          onClick={
                            canNavigate
                              ? () => navigate("/billing?aging=120")
                              : undefined
                          }
                          disabled={!canNavigate}
                        />
                      </Col>
                    </Row>
                  </div>
                </Collapse>
              </SectionCard>

              <SectionCard
                title={
                  <button
                    type="button"
                    className="section-card__title-toggle"
                    onClick={() => setShowClaims((v) => { localStorage.setItem("home_showClaims", String(!v)); return !v; })}
                    aria-expanded={showClaims}
                    aria-label={showClaims ? 'Collapse claims' : 'Expand claims'}
                  >
                    Electronic Claims
                    <i className={`fa-solid fa-chevron-${showClaims ? "up" : "down"} ms-2`} />
                  </button>
                }
                subtitle="Past 30 days"
                className="mobile-top-divider"
              >
                <Collapse in={showClaims}>
                  <div>
                    <Row xs={2} md={2} className="g-2">
                      <Col>
                        <StatCard
                          dense
                          label="Submitted"
                          value={claimTotals.submitted}
                          tone="primary"
                          onClick={
                            canNavigate
                              ? () => {
                                  // Forward the dashboard's 30-day window so
                                  // the Claims report opens with the same
                                  // filter and totals match.
                                  const today = new Date();
                                  const start = new Date();
                                  start.setMonth(start.getMonth() - 1);
                                  const iso = (d) => d.toISOString().slice(0, 10);
                                  navigate(
                                    `/billing?section=claims&startDate=${iso(start)}&endDate=${iso(today)}`
                                  );
                                }
                              : undefined
                          }
                          disabled={!canNavigate}
                        />
                      </Col>
                      <Col>
                        <StatCard
                          dense
                          label="Paid"
                          value={claimTotals.paid}
                          tone="success"
                        />
                      </Col>
                    </Row>
                  </div>
                </Collapse>
              </SectionCard>
            </>
          )}
        </Col>
      </Row>

      {/* Modals (unchanged) */}
      <AddPatientSteps
        show={modalShow}
        onHide={() => setModalShow(false)}
        appointmentId={"1"}
        onSaved={() => {
          setModalShow(false);
          setListRefreshKey((k) => k + 1);
        }}
      />
      {receiptModalShow && (
        <CreateReceiptModal
          show={receiptModalShow}
          onHide={() => setReceiptModalShow(false)}
          patient={patient}
        />
      )}
    </div>
  );
}
